import React, { useState } from 'react';

/**
 * Tiny reusable Edit/Delete control set for table rows and cards.
 *
 * Props:
 *  - onEdit: () => void                 (optional — hides Edit if not provided)
 *  - onDelete: (force?: bool) => Promise (optional — hides Delete if not provided)
 *  - confirmTitle: string               default "Delete this item?"
 *  - confirmText:  string               default friendly explanation
 *  - dangerLabel:  string               default "Delete"
 *  - testIdPrefix: string               for data-testid hooks
 *  - size: 'sm' | 'md'                  visual size
 *  - inline: bool                       horizontal vs stacked
 */
export function RowActions({
  onEdit,
  onDelete,
  confirmTitle = 'Delete this item?',
  confirmText = 'This action cannot be undone. Records with history will be archived (soft-deleted) so reports stay intact.',
  dangerLabel = 'Delete',
  testIdPrefix = 'row',
  size = 'sm',
  inline = true,
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [needsForce, setNeedsForce] = useState(false);

  const btnPad = size === 'md' ? 'px-3 py-2 text-sm' : 'px-2 py-1 text-xs';

  const runDelete = async (force = false) => {
    setBusy(true);
    setServerError(null);
    try {
      await onDelete(force);
      setShowConfirm(false);
      setNeedsForce(false);
    } catch (err) {
      const msg = err?.message || 'Failed to delete';
      setServerError(msg);
      // 409 Conflict (dependents) → suggest force/archive flow
      if (/dependent|reference|409/i.test(msg)) {
        setNeedsForce(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={inline ? 'inline-flex items-center gap-2' : 'flex flex-col gap-2'}>
        {onEdit && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className={`${btnPad} rounded-md border border-cyan-200 bg-cyan-50 text-cyan-800 hover:bg-cyan-100 transition-colors font-medium`}
            data-testid={`${testIdPrefix}-edit-btn`}
            aria-label="Edit"
          >
            ✏️ Edit
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
            className={`${btnPad} rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors font-medium`}
            data-testid={`${testIdPrefix}-delete-btn`}
            aria-label={dangerLabel}
          >
            🗑️ {dangerLabel}
          </button>
        )}
      </div>

      {showConfirm && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          onClick={() => !busy && setShowConfirm(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testIdPrefix}-confirm-dialog`}
          >
            <h3 className="text-lg font-bold text-slate-900 mb-2">{confirmTitle}</h3>
            <p className="text-sm text-slate-600 mb-4">{confirmText}</p>
            {serverError && (
              <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800" data-testid={`${testIdPrefix}-confirm-error`}>
                {serverError}
              </div>
            )}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={busy}
                className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid={`${testIdPrefix}-confirm-cancel`}
              >
                Cancel
              </button>
              {needsForce ? (
                <button
                  type="button"
                  onClick={() => runDelete(true)}
                  disabled={busy}
                  className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                  data-testid={`${testIdPrefix}-confirm-force`}
                >
                  {busy ? 'Working…' : 'Force delete (remove dependents first)'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => runDelete(false)}
                  disabled={busy}
                  className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                  data-testid={`${testIdPrefix}-confirm-ok`}
                >
                  {busy ? 'Working…' : dangerLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RowActions;
